import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut, Monitor, Moon, Settings, Sun, UserRound } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate, useNavigation } from 'react-router';
import { Wordmark } from '@/components/brand';
import { PrimaryNav } from '@/components/shell/primary-nav';
import { ServerTree } from '@/components/shell/server-tree';
import { iconButtonClasses, skipLinkClasses } from '@/components/shell/shell-classes';
import { useMediaQuery } from '@/components/shell/use-media-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/toast';
import { useServerEvents } from '@/lib/api/events';
import { api } from '@/lib/api/instance';
import { useSession } from '@/lib/api/queries';
import { UNGUARDED_NAVIGATION } from '@/lib/navigation';
import type { ThemePreference } from '@/lib/theme';
import { useTheme } from '@/lib/theme';

/** Media query of the rail layout (the lg breakpoint). */
const RAIL_QUERY = '(width >= 64rem)';

/** Menus open above their trigger and start-aligned on the rail, below and end-aligned in the top bar. */
function useMenuPlacement() {
  const rail = useMediaQuery(RAIL_QUERY);
  return { side: rail ? 'top' : 'bottom', align: rail ? 'start' : 'end' } as const;
}

function ThemeMenu() {
  const { preference, resolved, setPreference } = useTheme();
  const placement = useMenuPlacement();
  const Icon = resolved === 'dark' ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Color theme" className={iconButtonClasses}>
          <Icon aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-40" {...placement}>
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => setPreference(value as ThemePreference)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun aria-hidden /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon aria-hidden /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor aria-hidden /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      queryClient.clear();
      navigate('/login', { replace: true, state: UNGUARDED_NAVIGATION });
    },
  });
}

function UserMenu() {
  const session = useSession();
  const signOut = useSignOut();
  const placement = useMenuPlacement();
  const email = session.data?.user.email ?? '';
  /**
   * Set when Settings is chosen. If the route change has already moved focus into the main region when the menu
   * closes, focus stays there instead of returning to the trigger.
   */
  const navigating = useRef(false);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Account menu" className={iconButtonClasses}>
          <UserRound aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56"
        {...placement}
        onCloseAutoFocus={(event) => {
          if (!navigating.current) return;
          navigating.current = false;
          if (document.getElementById('main')?.contains(document.activeElement)) {
            event.preventDefault();
          }
        }}
      >
        <DropdownMenuLabel className="whitespace-normal [overflow-wrap:anywhere]">
          Signed in as {email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          asChild
          onSelect={() => {
            navigating.current = true;
          }}
        >
          <Link to="/settings">
            <Settings aria-hidden /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => signOut.mutate()}>
          <LogOut aria-hidden /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The signed-in email (on the rail only), the theme menu and the account menu. On the rail the "Signed in as" label
 * shares the first row with the two menu buttons, and the email takes the full second row and wraps.
 */
function AccountControls() {
  const email = useSession().data?.user.email ?? '';
  return (
    <div className="ml-auto flex shrink-0 items-center gap-0.5 lg:mt-3 lg:ml-0 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:border-t lg:border-chrome-border lg:px-1 lg:pt-2.5">
      <div className="hidden lg:contents">
        <p className="px-1 text-xs text-chrome-muted lg:col-start-1 lg:row-start-1">Signed in as</p>
        <p className="px-1 text-sm text-chrome-text [overflow-wrap:anywhere] lg:col-span-3 lg:row-start-2">
          {email}
        </p>
      </div>
      <ThemeMenu />
      <UserMenu />
    </div>
  );
}

/**
 * The signed-in frame. One graphite header is both the full-height rail from lg (brand, primary nav, the server and
 * plugin tree, the account block) and the sticky top bar below lg, whose height is `--shell-offset`.
 */
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  const main = useRef<HTMLElement>(null);
  const focusedPath = useRef(location.pathname);
  /** After a route change, focus moves to the main region unless the new page already focused an element in it. */
  useEffect(() => {
    if (focusedPath.current === location.pathname) return;
    focusedPath.current = location.pathname;
    if (!main.current || main.current.contains(document.activeElement)) return;
    main.current.focus({ preventScroll: true });
  }, [location.pathname]);

  const onServerRemoved = useCallback(
    (serverId: string, name: string | undefined) => {
      if (pathRef.current.startsWith(`/servers/${serverId}`)) {
        toast.info(`${name ?? 'This server'} was removed`, {
          description: 'It is no longer connected to your account.',
        });
        navigate('/', { replace: true, state: UNGUARDED_NAVIGATION });
      }
    },
    [navigate],
  );
  useServerEvents(true, { onServerRemoved });
  const navigation = useNavigation();

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[var(--shell-rail-width)_minmax(0,1fr)]">
      <a href="#main" className={skipLinkClasses}>
        Skip to content
      </a>
      <header className="chrome sticky top-0 z-40 flex h-(--shell-offset) min-w-0 items-center gap-1 border-b border-chrome-border px-3 sm:px-4 lg:h-dvh lg:flex-col lg:items-stretch lg:gap-0 lg:self-start lg:border-r lg:border-b-0 lg:px-2.5 lg:py-3">
        <Link
          to="/"
          aria-label="ComfiConf home"
          className="focus-ring mr-1 flex h-8 shrink-0 items-center rounded-sm px-1.5 lg:mr-0 lg:mb-4"
        >
          <Wordmark
            tone="chrome"
            className="[&>span:last-child]:hidden min-[33rem]:[&>span:last-child]:inline"
          />
        </Link>
        <PrimaryNav />
        <ServerTree />
        <AccountControls />
      </header>
      {navigation.state === 'loading' ? (
        <div
          role="progressbar"
          aria-label="Loading page"
          className="fixed inset-x-0 top-(--shell-offset) z-40 h-0.5 overflow-hidden lg:left-(--shell-rail-width)"
        >
          <div className="h-full w-1/3 animate-progress bg-accent" />
        </div>
      ) : null}
      <main ref={main} id="main" tabIndex={-1} className="min-w-0 outline-none">
        <Outlet />
      </main>
    </div>
  );
}
